import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Globe,
  User,
  Plane,
  Sliders,
  Wind,
  Thermometer,
  BookMarked,
  Book,
  BookOpen,
  Link as LinkIcon,
  Info,
  ChevronRight,
  Check,
  Circle as CircleIcon,
  X,
  LogOut,
  Mail,
  CreditCard,
  Compass,
  Database,
  Shield,
  FileText,
  RotateCcw,
  Clock,
  IdCard,
  AlertTriangle,
  Cloud,
  LayoutGrid,
  Map as MapIcon,
  Trees,
  CalendarClock,
  ListChecks,
  Smartphone,
  PawPrint,
  type LucideIcon,
} from "lucide-react";
import { apiJson } from "../lib/queryClient";
import { useAuth } from "../lib/auth";
import { SORTED_COUNTRY_MAPS, getCountryMapConfig, getCountryFlag } from "../lib/countryMaps";
import { WildlifeRescueInfoModal } from "../components/WildlifeRescueInfo";
import {
  WILDLIFE_RESCUE_MODE_TITLE,
  WILDLIFE_RESCUE_MODE_SUBTITLE,
  WILDLIFE_RESCUE_CONFIRM_QUESTION,
  WILDLIFE_RESCUE_THANK_YOU,
} from "@shared/wildlifeRescue";

type Settings = {
  id: string;
  windUnit: "m/s" | "km/h";
  tempUnit: "C" | "F";
  preferGermany: boolean;
  preferredCountryIso: string;
  officialMapCrossCheck: boolean;
  attachPilotDocsToExports: boolean;
  wildlifeRescueMode: boolean;
  limits: {
    maxWind: number;
    maxGusts: number;
    maxRain: number;
    minTemp: number;
    maxTemp: number;
    minCloudCeiling: number;
    minVisibility: number;
  };
};

const DEFAULT: Omit<Settings, "id"> = {
  windUnit: "m/s",
  tempUnit: "C",
  preferGermany: true,
  preferredCountryIso: "DE",
  officialMapCrossCheck: true,
  attachPilotDocsToExports: true,
  wildlifeRescueMode: false,
  limits: {
    maxWind: 10,
    maxGusts: 12,
    maxRain: 0.5,
    minTemp: 0,
    maxTemp: 40,
    minCloudCeiling: 50,
    minVisibility: 5000,
  },
};

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery<Settings | null>({ queryKey: ["/api/settings"] });
  const [form, setForm] = useState<Omit<Settings, "id">>(DEFAULT);
  const [savedFlash, setSavedFlash] = useState(false);
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [wildlifeConfirmOpen, setWildlifeConfirmOpen] = useState(false);
  const [wildlifeThanksOpen, setWildlifeThanksOpen] = useState(false);
  const [wildlifeInfoOpen, setWildlifeInfoOpen] = useState(false);

  useEffect(() => {
    if (data) {
      setForm({
        windUnit: (data.windUnit as "m/s" | "km/h") || "m/s",
        tempUnit: (data.tempUnit as "C" | "F") || "C",
        preferGermany: data.preferGermany,
        preferredCountryIso: (data.preferredCountryIso || "DE").toUpperCase(),
        officialMapCrossCheck: data.officialMapCrossCheck ?? true,
        attachPilotDocsToExports: data.attachPilotDocsToExports ?? true,
        wildlifeRescueMode: data.wildlifeRescueMode ?? false,
        limits: { ...DEFAULT.limits, ...data.limits },
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (payload: Omit<Settings, "id">) => apiJson("POST", "/api/settings", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/settings"] });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    },
  });

  function persist(next: Omit<Settings, "id">) {
    setForm(next);
    save.mutate(next);
  }

  function selectCountry(iso: string) {
    persist({ ...form, preferredCountryIso: iso.toUpperCase() });
    setCountryPickerOpen(false);
  }

  function toggleWindUnit() {
    persist({ ...form, windUnit: form.windUnit === "m/s" ? "km/h" : "m/s" });
  }

  function toggleTempUnit() {
    persist({ ...form, tempUnit: form.tempUnit === "C" ? "F" : "C" });
  }

  function toggleAttachDocs() {
    persist({ ...form, attachPilotDocsToExports: !form.attachPilotDocsToExports });
  }

  function toggleWildlifeRescue() {
    if (form.wildlifeRescueMode) {
      persist({ ...form, wildlifeRescueMode: false });
    } else {
      setWildlifeConfirmOpen(true);
    }
  }

  function confirmWildlifeRescue() {
    persist({ ...form, wildlifeRescueMode: true });
    setWildlifeConfirmOpen(false);
    setWildlifeThanksOpen(true);
  }

  function setLimit<K extends keyof Settings["limits"]>(key: K, value: number) {
    persist({ ...form, limits: { ...form.limits, [key]: value } });
  }

  const currentCountry = useMemo(
    () => getCountryMapConfig(form.preferredCountryIso)?.nameDE ?? "Deutschland",
    [form.preferredCountryIso],
  );

  return (
    <div className="space-y-7 max-w-2xl mx-auto pb-6">
      {/* KONTO */}
      <Section title="Konto">
        <div className="ios-list">
          <ValueRow icon={Mail} label="E-Mail">
            <span className="text-[13px] font-medium text-[var(--color-app-text-secondary)] truncate max-w-[58%]">
              {user?.email}
            </span>
          </ValueRow>
          <ValueRow icon={User} label="Name">
            <span className="text-[13px] font-medium text-[var(--color-app-text-secondary)] truncate max-w-[58%]">
              {user?.name || "—"}
            </span>
          </ValueRow>
          <LinkRow to="/abo" icon={CreditCard} label="Abo" sublabel="Abo-Status & Verwaltung" />
          <LinkRow to="/meine-daten" icon={Database} label="Meine Daten" />
          <ActionRow icon={LogOut} label="Abmelden" danger onClick={logout} testId="button-logout" />
        </div>
      </Section>

      {/* FLUGREGION */}
      <Section title="Flugregion">
        <div className="ios-list">
          <button
            type="button"
            onClick={() => setCountryPickerOpen(true)}
            className="ios-row w-full text-left"
            data-testid="button-country-picker"
          >
            <span className="ios-row-icon">
              <Globe size={20} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="flex items-center justify-between gap-2">
                <span className="text-[15px] text-[var(--color-app-text)]">Land</span>
                <span className="text-[14px] font-semibold text-brand-600 truncate">
                  {currentCountry}
                </span>
              </span>
              <span className="block text-[12.5px] text-[var(--color-app-text-secondary)] mt-0.5">
                Bestimmt Karten-Zentrierung, NOTAMs &amp; Geozonen-Filter
              </span>
            </span>
            <ChevronRight size={18} className="text-[var(--color-app-text-tertiary)] shrink-0" />
          </button>
        </div>
      </Section>

      {/* PILOT */}
      <Section title="Pilot">
        <div className="ios-list">
          <LinkRow to="/profil" icon={User} label="Pilotenprofil" />
          <LinkRow to="/lizenzkarte" icon={IdCard} label="Führerschein anzeigen" />
          <LinkRow to="/ablauf" icon={Clock} label="Ablaufende Dokumente" />
          <LinkRow to="/einrichtung" icon={Compass} label="Einrichtungs-Assistent" />
        </div>
      </Section>

      {/* DROHNEN */}
      <Section title="Drohnen">
        <div className="ios-list">
          <LinkRow to="/drohnen" icon={Plane} label="Alle Drohnen verwalten" />
        </div>
      </Section>

      {/* KARTEN & PLANUNG */}
      <Section title="Karten & Planung">
        <div className="ios-list">
          <LinkRow to="/" icon={LayoutGrid} label="Übersicht" />
          <LinkRow to="/karte" icon={MapIcon} label="Karte & NOTAMs" />
          <LinkRow to="/naturschutz" icon={Trees} label="Naturschutzgebiete" />
          <LinkRow to="/wetter" icon={Cloud} label="Wetter" />
          <LinkRow to="/flugplanung" icon={CalendarClock} label="Flugplanung" />
          <LinkRow to="/fluege" icon={ListChecks} label="Flüge" />
        </div>
      </Section>

      {/* TCAS */}
      <Section title="TCAS – Proximity-Alarm">
        <div className="ios-list">
          <LinkRow to="/tcas" icon={AlertTriangle} label="TCAS – Proximity-Alarm" />
        </div>
      </Section>

      {/* WETTERGRENZEN */}
      <Section title="Wettergrenzen">
        <div className="ios-list">
          <button
            type="button"
            onClick={() => setLimitsOpen((v) => !v)}
            className="ios-row w-full text-left"
            data-testid="button-toggle-limits"
          >
            <span className="ios-row-icon">
              <Sliders size={20} />
            </span>
            <span className="flex-1 text-[15px] text-[var(--color-app-text)]">Globale Grenzwerte</span>
            <ChevronRight
              size={18}
              className={`text-[var(--color-app-text-tertiary)] shrink-0 transition-transform ${limitsOpen ? "rotate-90" : ""}`}
            />
          </button>
        </div>
        {limitsOpen ? (
          <div className="card mt-2 p-4 space-y-3">
            <p className="text-xs text-[var(--color-app-text-secondary)] -mt-1">
              Diese Limits werden für die Go/Caution/No-Go Bewertung verwendet.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Max. Wind (m/s)" value={form.limits.maxWind} onChange={(v) => setLimit("maxWind", v)} />
              <NumField label="Max. Böen (m/s)" value={form.limits.maxGusts} onChange={(v) => setLimit("maxGusts", v)} />
              <NumField label="Max. Regen (mm/h)" value={form.limits.maxRain} step={0.1} onChange={(v) => setLimit("maxRain", v)} />
              <NumField label="Min. Temperatur (°C)" value={form.limits.minTemp} onChange={(v) => setLimit("minTemp", v)} />
              <NumField label="Max. Temperatur (°C)" value={form.limits.maxTemp} onChange={(v) => setLimit("maxTemp", v)} />
              <NumField label="Min. Wolkenbasis (m)" value={form.limits.minCloudCeiling} onChange={(v) => setLimit("minCloudCeiling", v)} />
              <NumField label="Min. Sichtweite (m)" value={form.limits.minVisibility} onChange={(v) => setLimit("minVisibility", v)} />
            </div>
            {savedFlash ? (
              <p className="text-xs text-go bg-go-bg rounded px-2 py-1">Gespeichert</p>
            ) : null}
          </div>
        ) : null}
      </Section>

      {/* EINHEITEN */}
      <Section title="Einheiten">
        <div className="ios-list">
          <ValueRow icon={Wind} label="Windgeschwindigkeit">
            <button
              type="button"
              onClick={toggleWindUnit}
              className="px-3 py-1 rounded-lg bg-[var(--color-app-bg-tertiary)] hover:opacity-80 text-sm font-semibold text-brand-600 transition"
              data-testid="button-toggle-wind-unit"
            >
              {form.windUnit}
            </button>
          </ValueRow>
          <ValueRow icon={Thermometer} label="Temperatur">
            <button
              type="button"
              onClick={toggleTempUnit}
              className="px-3 py-1 rounded-lg bg-[var(--color-app-bg-tertiary)] hover:opacity-80 text-sm font-semibold text-brand-600 transition"
              data-testid="button-toggle-temp-unit"
            >
              {form.tempUnit === "F" ? "°F" : "°C"}
            </button>
          </ValueRow>
        </div>
      </Section>

      {/* DOKUMENTE */}
      <Section title="Dokumente">
        <div className="ios-list">
          <ValueRow icon={FileText} label="Dokumente an Export anhängen">
            <button
              type="button"
              onClick={toggleAttachDocs}
              role="switch"
              aria-checked={form.attachPilotDocsToExports}
              className={`px-3 py-1 rounded-lg hover:opacity-80 text-sm font-semibold transition ${
                form.attachPilotDocsToExports
                  ? "bg-brand-600 text-white"
                  : "bg-[var(--color-app-bg-tertiary)] text-[var(--color-app-text-secondary)]"
              }`}
              data-testid="button-toggle-attach-docs"
            >
              {form.attachPilotDocsToExports ? "An" : "Aus"}
            </button>
          </ValueRow>
        </div>
        <p className="text-xs text-[var(--color-app-text-secondary)] mt-2 px-1">
          Hängt die zum Flugzeitpunkt gültige Allgemeinverfügung, A2-Lizenz und den
          Versicherungsnachweis an PDF-Exporte an. Beim Export einzeln wählbar.
        </p>
      </Section>

      {/* WILDTIERRETTUNG */}
      <Section title="Wildtierrettung">
        <div className="ios-list">
          <ValueRow icon={PawPrint} label={WILDLIFE_RESCUE_MODE_TITLE}>
            <button
              type="button"
              onClick={toggleWildlifeRescue}
              role="switch"
              aria-checked={form.wildlifeRescueMode}
              className={`px-3 py-1 rounded-lg hover:opacity-80 text-sm font-semibold transition ${
                form.wildlifeRescueMode
                  ? "bg-brand-600 text-white"
                  : "bg-[var(--color-app-bg-tertiary)] text-[var(--color-app-text-secondary)]"
              }`}
              data-testid="button-toggle-wildlife-rescue"
            >
              {form.wildlifeRescueMode ? "An" : "Aus"}
            </button>
          </ValueRow>
          {form.wildlifeRescueMode ? (
            <button
              type="button"
              onClick={() => setWildlifeInfoOpen(true)}
              className="ios-row w-full text-left"
              data-testid="button-wildlife-more-info"
            >
              <span className="ios-row-icon">
                <Info size={18} />
              </span>
              <span className="flex-1 text-[15px] text-[var(--color-app-text)]">
                Mehr Infos zu den Allgemeinverfügungen
              </span>
              <ChevronRight size={18} className="text-[var(--color-app-text-tertiary)] shrink-0" />
            </button>
          ) : null}
        </div>
        <p className="text-xs text-[var(--color-app-text-secondary)] mt-2 px-1">
          {WILDLIFE_RESCUE_MODE_SUBTITLE}
        </p>
      </Section>

      {/* RESSOURCEN */}
      <Section title="Ressourcen">
        <div className="ios-list">
          <LinkRow to="/ressourcen" icon={LinkIcon} label="Nützliche Links" />
          <LinkRow to="/glossar" icon={Book} label="Glossar" />
          <LinkRow to="/easa" icon={BookOpen} label="EASA — EU-Drohnenregeln" />
          <LinkRow to="/regelwerk" icon={FileText} label="Easy Access Rules (UAS)" />
          <LinkRow to="/laender" icon={Globe} label="Länder — Rules & Regulations" />
        </div>
      </Section>

      {/* ADMIN */}
      {user?.isAdmin ? (
        <Section title="Administration">
          <div className="ios-list">
            <LinkRow to="/admin" icon={Shield} label="Admin" />
            <LinkRow to="/admin/abos" icon={CreditCard} label="Abos & Pakete" />
          </div>
        </Section>
      ) : null}

      {/* ÜBER */}
      <Section title="Über">
        <div className="ios-list">
          <ValueRow icon={Info} label="Version">
            <span className="text-sm text-[var(--color-app-text-tertiary)]">1.0.0</span>
          </ValueRow>
          <LinkRow to="/datenschutz" icon={Shield} label="Datenschutzerklärung" />
          <LinkRow to="/agb" icon={FileText} label="AGB / Nutzungsbedingungen" />
          <LinkRow to="/widerruf" icon={RotateCcw} label="Widerrufsbelehrung" />
          <LinkRow to="/impressum" icon={Info} label="Impressum" />
          <ExternalRow to="/expo" icon={Smartphone} label="Mobile App (Expo Go)" />
        </div>
        <p className="px-2 mt-3 text-[11px] text-[var(--color-app-text-tertiary)] leading-relaxed">
          Die Web-App dient ausschließlich der <strong>Vorab-Planung</strong> und der Verwaltung
          deiner Pre-Flight Checks &amp; Logbücher. Live-Flugüberwachung (z. B. Verkehrsradar)
          findet ausschließlich in der mobilen App statt.
        </p>
      </Section>

      {/* Country picker modal */}
      {countryPickerOpen ? (
        <CountryPicker
          selectedIso={form.preferredCountryIso}
          onSelect={selectCountry}
          onClose={() => setCountryPickerOpen(false)}
        />
      ) : null}

      {/* Wildtierretter-Bestätigung */}
      {wildlifeConfirmOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setWildlifeConfirmOpen(false)}
          data-testid="modal-wildlife-confirm"
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-[var(--color-app-text)] mb-2">
              {WILDLIFE_RESCUE_MODE_TITLE}
            </h3>
            <p className="text-sm text-[var(--color-app-text-secondary)] mb-4">
              {WILDLIFE_RESCUE_CONFIRM_QUESTION}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setWildlifeConfirmOpen(false)}
                className="px-4 py-2 rounded-lg bg-[var(--color-app-bg-tertiary)] text-[var(--color-app-text)] text-sm font-semibold hover:opacity-80"
                data-testid="button-wildlife-confirm-no"
              >
                Nein
              </button>
              <button
                type="button"
                onClick={confirmWildlifeRescue}
                className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:opacity-90"
                data-testid="button-wildlife-confirm-yes"
              >
                Ja
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Wildtierretter-Dank */}
      {wildlifeThanksOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setWildlifeThanksOpen(false)}
          data-testid="modal-wildlife-thanks"
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm text-[var(--color-app-text)] mb-4" data-testid="text-wildlife-thanks">
              {WILDLIFE_RESCUE_THANK_YOU}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setWildlifeThanksOpen(false);
                  setWildlifeInfoOpen(true);
                }}
                className="px-4 py-2 rounded-lg bg-[var(--color-app-bg-tertiary)] text-[var(--color-app-text)] text-sm font-semibold hover:opacity-80"
                data-testid="button-wildlife-thanks-info"
              >
                Mehr Infos
              </button>
              <button
                type="button"
                onClick={() => setWildlifeThanksOpen(false)}
                className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:opacity-90"
                data-testid="button-wildlife-thanks-close"
              >
                Over and Out
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <WildlifeRescueInfoModal open={wildlifeInfoOpen} onClose={() => setWildlifeInfoOpen(false)} />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-[12px] font-bold tracking-wider text-[var(--color-app-text-tertiary)] uppercase mb-2 px-1">
        {title}
      </h2>
      {children}
    </section>
  );
}

function ValueRow({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="ios-row">
      <span className="ios-row-icon">
        <Icon size={20} />
      </span>
      <span className="flex-1 text-[15px] text-[var(--color-app-text)]">{label}</span>
      {children}
    </div>
  );
}

function LinkRow({
  to,
  icon: Icon,
  label,
  sublabel,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  sublabel?: string;
}) {
  return (
    <Link to={to} className="ios-row" data-testid={`settings-${to.replace(/\//g, "-")}`}>
      <span className="ios-row-icon">
        <Icon size={20} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] text-[var(--color-app-text)]">{label}</span>
        {sublabel ? (
          <span className="block text-[12.5px] text-[var(--color-app-text-secondary)] mt-0.5">
            {sublabel}
          </span>
        ) : null}
      </span>
      <ChevronRight size={18} className="text-[var(--color-app-text-tertiary)] shrink-0" />
    </Link>
  );
}

function ExternalRow({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
}) {
  return (
    <a
      href={to}
      target="_blank"
      rel="noopener noreferrer"
      className="ios-row"
      data-testid={`settings-ext-${to.replace(/\//g, "-")}`}
    >
      <span className="ios-row-icon">
        <Icon size={20} />
      </span>
      <span className="flex-1 text-[15px] text-[var(--color-app-text)]">{label}</span>
      <ChevronRight size={18} className="text-[var(--color-app-text-tertiary)] shrink-0" />
    </a>
  );
}

function ActionRow({
  icon: Icon,
  label,
  onClick,
  danger,
  testId,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  danger?: boolean;
  testId?: string;
}) {
  const color = danger ? "#ef4444" : undefined;
  return (
    <button type="button" onClick={onClick} className="ios-row w-full text-left" data-testid={testId}>
      <span className="ios-row-icon" style={danger ? { background: "rgba(239,68,68,0.12)", color } : undefined}>
        <Icon size={20} />
      </span>
      <span className="flex-1 text-[15px] font-medium" style={color ? { color } : undefined}>
        {label}
      </span>
    </button>
  );
}

function NumField({
  label,
  value,
  onChange,
  step = 1,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
}) {
  return (
    <div>
      <label className="text-xs text-[var(--color-app-text-secondary)] block mb-1">{label}</label>
      <input
        type="number"
        step={step}
        className="input"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      />
    </div>
  );
}

function CountryPicker({
  selectedIso,
  onSelect,
  onClose,
}: {
  selectedIso: string;
  onSelect: (iso: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-md max-h-[85%] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pt-2 pb-1 flex justify-center sm:hidden">
          <div className="w-10 h-1 rounded-full bg-[var(--color-app-bg-tertiary)]" />
        </div>
        <div className="px-5 pt-3 pb-2 flex items-center justify-between">
          <h3 className="text-lg font-bold text-[var(--color-app-text)]">Land wählen</h3>
          <button onClick={onClose} className="size-8 rounded-full hover:bg-[var(--color-app-bg-tertiary)] flex items-center justify-center" aria-label="Schließen">
            <X size={18} className="text-[var(--color-app-text-secondary)]" />
          </button>
        </div>
        <p className="px-5 text-xs text-[var(--color-app-text-secondary)] pb-3">
          Karten, NOTAMs und Geozonen werden auf dieses Land beschränkt. Du kannst es jederzeit hier wechseln.
        </p>
        <div className="overflow-y-auto px-3 pb-4 space-y-1.5">
          {SORTED_COUNTRY_MAPS.map((c) => {
            const selected = c.iso === selectedIso.toUpperCase();
            return (
              <button
                key={c.iso}
                type="button"
                onClick={() => onSelect(c.iso)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition text-left ${
                  selected
                    ? "border-brand-500 bg-brand-50"
                    : "border-transparent bg-[var(--color-app-bg-secondary)] hover:bg-[var(--color-app-bg-tertiary)]"
                }`}
                data-testid={`country-${c.iso}`}
              >
                <span className="text-2xl leading-none">{getCountryFlag(c.iso)}</span>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[var(--color-app-text)]">{c.nameDE}</div>
                  <div className="text-xs text-[var(--color-app-text-secondary)]">{c.iso}</div>
                </div>
                {selected ? (
                  <Check size={20} className="text-brand-600" />
                ) : (
                  <CircleIcon size={20} className="text-[var(--color-app-text-tertiary)]" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
